// The last good emote lists, kept in localStorage so an overlay that starts
// while 7TV, BetterTTV or FrankerFaceZ is down still draws the emotes it drew
// last time. OBS keeps a browser source's storage across restarts.
//
// A saved list is a stand-in, never an answer: it is drawn from until its
// provider answers (or while it is down), and every fresh answer (and every live
// 7TV edit or set switch the overlay applied) is written straight back over it.
//
// Global lists are kept once; channel lists for the few most recently used
// channels only, so the page's storage stays small. Every read and write is
// guarded: storage can be missing, full or corrupt, and the overlay works
// without it.

import type { EmoteInfo, EmoteLayer } from './emoteMap';

const PREFIX = 'sn_emotes_v1:';
const ROOMS_KEY = `${PREFIX}rooms`;
/** Channels whose lists are kept; the least recently used goes first. */
export const MAX_SAVED_ROOMS = 8;
/** A single list larger than this is not kept (no real set comes near it). */
const MAX_ENTRY_CHARS = 1_000_000;

export type EmoteSource = 'ffz' | 'bttv' | '7tv';

/** One saved list. `seventvSetId` / `seventvUserId` ride with a channel's 7TV
 *  list so a restored list still follows live edits to that set. */
export interface SavedEmoteList {
  layer: EmoteLayer;
  seventvSetId?: string | null;
  seventvUserId?: string | null;
  savedAt: number;
}

// [name, url, zeroWidth, 7TV id ('' for none), modifier flags]
type Row = [string, string, 0 | 1, string?, number?];

// Modifier flags: which emote a modifier attaches to, and whether it draws.
const ATTACHES_PREVIOUS = 1;
const ATTACHES_NEXT = 2;
const HIDDEN = 4;

function modifierFlags(info: EmoteInfo): number {
  return (info.attaches === 'previous' ? ATTACHES_PREVIOUS : 0) | (info.attaches === 'next' ? ATTACHES_NEXT : 0) | (info.hidden ? HIDDEN : 0);
}

interface Stored {
  rows: Row[];
  setId?: string | null;
  userId?: string | null;
  at: number;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function keyOf(scope: string, source: EmoteSource): string {
  return `${PREFIX}${scope}:${source}`;
}

/** `scope` is 'global', a Twitch room id, or `kick:<id>` / `google:<id>` (emoteScope). */
export function loadSavedList(scope: string, source: EmoteSource): SavedEmoteList | null {
  const ls = storage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(keyOf(scope, source));
    if (!raw) return null;
    const stored = JSON.parse(raw) as Stored;
    if (!Array.isArray(stored?.rows)) return null;
    const layer: EmoteLayer = new Map();
    for (const r of stored.rows) {
      if (!Array.isArray(r) || typeof r[0] !== 'string' || typeof r[1] !== 'string') continue;
      const info: EmoteInfo = { url: r[1], zeroWidth: r[2] === 1 };
      if (typeof r[3] === 'string' && r[3]) info.id = r[3];
      const flags = typeof r[4] === 'number' ? r[4] : 0;
      if (flags & ATTACHES_PREVIOUS) info.attaches = 'previous';
      else if (flags & ATTACHES_NEXT) info.attaches = 'next';
      if (flags & HIDDEN) info.hidden = true;
      layer.set(r[0], info);
    }
    return {
      layer,
      seventvSetId: stored.setId ?? null,
      seventvUserId: stored.userId ?? null,
      savedAt: typeof stored.at === 'number' ? stored.at : 0,
    };
  } catch {
    return null;
  }
}

/** Keep `layer` as the last good list for `scope`/`source`. */
export function saveList(
  scope: string,
  source: EmoteSource,
  layer: EmoteLayer,
  ids?: { seventvSetId: string | null; seventvUserId: string | null },
  /** A list kept only in a spare slot (a Shared Chat partner's): never saved
   *  in place of the overlay's own channels, and the first to go. */
  spare = false,
): void {
  const ls = storage();
  if (!ls) return;
  if (spare && scope !== 'global' && !readRooms(ls).includes(scope) && readRooms(ls).length >= MAX_SAVED_ROOMS) return;
  const rows: Row[] = [];
  for (const [name, info] of layer) {
    // Personal emotes belong to a chatter, not the channel's list.
    if (info.personal) continue;
    const flags = modifierFlags(info);
    const zw = info.zeroWidth ? 1 : 0;
    if (flags) rows.push([name, info.url, zw, info.id ?? '', flags]);
    else rows.push(info.id ? [name, info.url, zw, info.id] : [name, info.url, zw]);
  }
  const stored: Stored = { rows, at: Date.now() };
  if (ids) {
    stored.setId = ids.seventvSetId;
    stored.userId = ids.seventvUserId;
  }
  let raw: string;
  try {
    raw = JSON.stringify(stored);
  } catch {
    return;
  }
  if (raw.length > MAX_ENTRY_CHARS) return;
  if (scope !== 'global') touchRoom(ls, scope, spare);
  try {
    ls.setItem(keyOf(scope, source), raw);
  } catch {
    // Full: give up the least recently used other channel once and try again
    // (never for a spare list).
    if (!spare && evictOldestRoom(ls, scope)) {
      try {
        ls.setItem(keyOf(scope, source), raw);
      } catch {
        /* the overlay still works; it just has nothing saved for this list */
      }
    }
  }
}

function readRooms(ls: Storage): string[] {
  try {
    const rooms = JSON.parse(ls.getItem(ROOMS_KEY) ?? '[]');
    return Array.isArray(rooms) ? rooms.filter((r): r is string => typeof r === 'string') : [];
  } catch {
    return [];
  }
}

function writeRooms(ls: Storage, rooms: string[]) {
  try {
    ls.setItem(ROOMS_KEY, JSON.stringify(rooms));
  } catch {
    /* ignore */
  }
}

function dropRoom(ls: Storage, room: string) {
  for (const source of ['ffz', 'bttv', '7tv'] as const) {
    try {
      ls.removeItem(keyOf(room, source));
    } catch {
      /* ignore */
    }
  }
}

/** Mark `room` most recently used (a spare one least recently used, so it is
 *  the first to go), dropping channels past MAX_SAVED_ROOMS. */
function touchRoom(ls: Storage, room: string, spare = false) {
  const before = readRooms(ls);
  if (spare && before.includes(room)) return;
  const rooms = before.filter((r) => r !== room);
  if (spare) rooms.unshift(room);
  else rooms.push(room);
  while (rooms.length > MAX_SAVED_ROOMS) dropRoom(ls, rooms.shift()!);
  writeRooms(ls, rooms);
}

function evictOldestRoom(ls: Storage, keep: string): boolean {
  const rooms = readRooms(ls);
  const victim = rooms.find((r) => r !== keep);
  if (!victim) return false;
  dropRoom(ls, victim);
  writeRooms(ls, rooms.filter((r) => r !== victim));
  return true;
}
